import React from 'react';
import { 
  CheckCircleIcon, 
  HandThumbUpIcon,
  HandThumbDownIcon,
  ArchiveBoxIcon,
  PaperAirplaneIcon,
  PhoneIcon,
  EyeIcon,
  BoltIcon,
  EnvelopeIcon
} from '@heroicons/react/24/solid';

// Helper function to format date relative to now
const formatRelativeDate = (timestamp) => {
  if (!timestamp) return '';
  
  // Convert Firebase timestamp to Date if needed
  const date = timestamp.seconds ? new Date(timestamp.seconds * 1000) : new Date(timestamp);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
  const diffMinutes = Math.floor(diffTime / (1000 * 60));
  
  if (diffMinutes < 1) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  
  // For older dates, show the actual date
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Function to format user name
const formatName = (fullName) => {
  if (!fullName) return 'Someone';
  
  // If it's an email, extract name part before @ and capitalize first letter
  if (fullName.includes('@')) {
    const name = fullName.split('@')[0];
    return name.charAt(0).toUpperCase() + name.slice(1);
  }
  
  // If it's a full name, just return first name
  const firstName = fullName.split(' ')[0];
  return firstName;
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const JobActivityFeed = ({ activities }) => {
  if (!activities || activities.length === 0) {
    return (
      <div className="py-3 text-sm text-gray-500 italic w-full">
        No activity recorded for this job yet.
      </div>
    );
  }

  // Sort activities with oldest first (chronological order)
  const sortedActivities = [...activities]
    .sort((a, b) => {
      const timeA = a.timestamp?.seconds ? a.timestamp.seconds : new Date(a.timestamp).getTime() / 1000;
      const timeB = b.timestamp?.seconds ? b.timestamp.seconds : new Date(b.timestamp).getTime() / 1000;
      return timeA - timeB; // Ascending order (oldest first)
    });

  // Get name of user who performed the action
  const getActorName = (activity) => {
    // For admin actions
    if (['recommended', 'archived', 'contacted_recruiter', 'fast_track_connect', 'fast_track_invite'].includes(activity.type)) {
      if (activity.admin) {
        return formatName(activity.admin.displayName || activity.admin.email || activity.admin.uid);
      }
      return formatName(activity.adminName || 'Admin');
    }
    
    // For user actions
    return formatName(activity.userName || activity.userEmail || 'User');
  };

  return (
    <ul role="list" className="space-y-4 w-full">
      {sortedActivities.map((activity, index) => (
        <li key={index} className="relative flex gap-x-4 w-full">
          {/* Timeline connector */}
          {index < sortedActivities.length - 1 && (
            <div className="absolute left-0 top-6 -bottom-6 flex w-6 justify-center">
              <div className="w-px bg-gray-200" />
            </div>
          )}
          
          {/* Activity icon */}
          <div className="relative flex size-6 flex-none items-center justify-center bg-white">
            {activity.type === 'recommended' && (
              <PaperAirplaneIcon className="h-5 w-5 text-indigo-500" />
            )}
            {activity.type === 'liked' && (
              <HandThumbUpIcon className="h-5 w-5 text-green-500" />
            )}
            {activity.type === 'disliked' && (
              <HandThumbDownIcon className="h-5 w-5 text-red-500" />
            )}
            {activity.type === 'archived' && (
              <ArchiveBoxIcon className="h-5 w-5 text-gray-500" />
            )}
            {activity.type === 'contacted_recruiter' && (
              <CheckCircleIcon className="h-5 w-5 text-blue-500" />
            )}
            {activity.type === 'viewed' && (
              <EyeIcon className="h-5 w-5 text-purple-500" />
            )}
            {activity.type === 'fast_track_connect' && (
              <BoltIcon className="h-5 w-5 text-amber-500" />
            )}
            {activity.type === 'fast_track_invite' && (
              <EnvelopeIcon className="h-5 w-5 text-teal-500" />
            )}
          </div>
          
          {/* Activity description */}
          <div className="flex-auto w-full">
            <div className="flex justify-between w-full">
              <p className="text-sm text-gray-700">
                {activity.type === 'recommended' && (
                  <>
                    <span className="font-medium text-indigo-600">
                      {getActorName(activity)}
                    </span> recommended this job
                  </>
                )}
                {activity.type === 'liked' && (
                  <>
                    <span className="font-medium text-green-600">
                      {getActorName(activity)}
                    </span> liked this job
                  </>
                )}
                {activity.type === 'disliked' && (
                  <>
                    <span className="font-medium text-red-600">
                      {getActorName(activity)}
                    </span> not interested
                  </>
                )}
                {activity.type === 'archived' && (
                  <>
                    <span className="font-medium text-gray-600">
                      {getActorName(activity)}
                    </span> archived this recommendation
                  </>
                )}
                {activity.type === 'contacted_recruiter' && (
                  <>
                    <span className="font-medium text-blue-600">
                      {getActorName(activity)}
                    </span> contacted the recruiter
                  </>
                )}
                {activity.type === 'viewed' && (
                  <>
                    <span className="font-medium text-purple-600">
                      {getActorName(activity)}
                    </span> viewed this job
                  </>
                )}
                {activity.type === 'fast_track_connect' && (
                  <>
                    <span className="font-medium text-amber-600">
                      {getActorName(activity)}
                    </span> sent request to Fast Track contact
                  </>
                )}
                {activity.type === 'fast_track_invite' && (
                  <>
                    <span className="font-medium text-teal-600">
                      {getActorName(activity)}
                    </span> sent fast track invite to user
                  </>
                )}
              </p>
              <time className="flex-none text-xs text-gray-500">
                {formatRelativeDate(activity.timestamp)}
              </time>
            </div>
            
            {/* Additional context/notes if present */}
            {activity.notes && (
              <p className="mt-1 text-sm text-gray-500 w-full">
                {activity.notes}
              </p>
            )}
            
            {/* Contact info for fast track activities */}
            {(activity.type === 'fast_track_connect' || activity.type === 'fast_track_invite') && activity.contact && (
              <div className="mt-2 p-2 bg-gray-50 rounded text-sm text-gray-700 border border-gray-200">
                <p className="font-medium">{activity.contact.firstName} {activity.contact.lastName}</p>
                {activity.contact.company && (
                  <p className="text-gray-600">{activity.contact.company}</p>
                )}
                <p className="text-gray-600">{activity.contact.email}</p>
                {activity.contact.availableTimes && (
                  <p className="mt-1 text-xs text-gray-500">Available: {activity.contact.availableTimes}</p>
                )}
              </div>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
};

export default JobActivityFeed; 